import React, { useMemo, useState } from 'react';
import { SkeletonLines } from '@/components/Admin/UI/Skeleton';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { colors } from '@/constants/colors';
import { IspPage } from './ispLayout';
import {
   getIspSmartfiBilling,
   convertCreditsForIsp,
   setIspSubscription,
} from '@/api/isp';

/**
 * ISP Business → Subscriptions (was "Billing"). What the OPERATOR owes SmartFi.
 *
 * ─── TWO CARDS FIRST: FREE AND PAID ───
 * Ray, 2026-10-29: *"they are confuse to billing… in free just limit the
 * automations features. right now im struggle to explain everything to
 * them."* So the page opens on the two choices, each saying what it includes,
 * before any number. Everything below is the Paid card's detail.
 *
 * ⚠️ NOT THE SAME BILLING AS THE VOUCHER STORE, which is why the menu item
 * carries a "SmartFi" pill (Ray, 2026-09-01). One screen is what this operator
 * charges their own subscribers; this one is what SmartFi charges them. Two
 * numbers that would be catastrophic to confuse.
 *
 * ─── PRICED IN PESOS, PAID IN POINTS ───
 * A credit is ₱500 and cannot express a ₱370 bill, so operators pay in points.
 * The bands hold money and the points are converted at `ispPointsPerCredit` —
 * a rate SmartFi sets separately from the loyalty one, so the discount a
 * distributor or reseller already got on the credit carries straight through
 * without any role logic in ISP billing at all.
 *
 * ─── CREDITS AND THE CONVERT BUTTON LIVE HERE ───
 * Ray, 2026-09-01: *"in the billing page, it shuld show his credits availale
 * with convert to points button."* A shortfall warning that sends somebody to
 * another screen to act on it is a dead end, and they will not come back.
 *
 * ─── RUNNING SHORT NEVER CUTS THEIR SUBSCRIBERS OFF ───
 * The only consequence is that they cannot ADD anybody. A shop's paying
 * customers losing internet because their ISP forgot to top up would punish the
 * people who did nothing wrong, and it is a reputation SmartFi does not get back.
 * The page says that plainly so an operator who is short is not frightened into
 * thinking their business is about to stop.
 */
const peso = (n: number) => `₱${num0(n)}`;

/**
 * Format a number that might not be there.
 *
 * 🔴 A MISSING FIELD MUST NOT WHITE-SCREEN THE PAGE. On 2026-09-04 the billing
 * endpoint's no-tenant branch omitted `creditBalance`, and
 * `num0(data.creditBalance)` threw straight through to the error
 * boundary — "Something went wrong" for every operator in prod, on a page whose
 * whole job is to reassure somebody about money. The server is fixed to return
 * one shape, but a screen that reads fifteen numbers should not be one typo in
 * an API away from showing nothing at all.
 */
const num0 = (v: unknown) => Number(v ?? 0).toLocaleString();

/** "September 2026" — a bill is remembered by its month, not by a date. */
const monthLabel = (iso: string) =>
   new Date(iso).toLocaleDateString('en-PH', {
      month: 'long',
      year: 'numeric',
      timeZone: 'UTC',
   });

const IspSmartfiBilling: React.FC = () => {
   const navigate = useNavigate();
   const qc = useQueryClient();
   const { data, isLoading } = useQuery({
      queryKey: ['isp-smartfi-billing'],
      queryFn: getIspSmartfiBilling,
   });

   const [credits, setCredits] = useState('');
   const [converting, setConverting] = useState(false);

   /**
    * When SmartFi next takes the month's points.
    *
    * Charges are raised on the 1st (`periodOf` on the server), so this needs
    * no new field — and computing it here keeps a stale server value from
    * ever showing a date that has passed.
    */
   const nextCharge = useMemo(() => {
      const now = new Date();
      const next = new Date(now.getFullYear(), now.getMonth() + 1, 1);
      const days = Math.max(
         0,
         Math.ceil((next.getTime() - now.getTime()) / 86400000)
      );
      return {
         days,
         label: next.toLocaleDateString('en-PH', {
            day: 'numeric',
            month: 'short',
         }),
      };
   }, []);

   const switchPlan = useMutation({
      mutationFn: (to: 'free' | 'paid') => setIspSubscription(to),
      onSuccess: (r) => {
         qc.invalidateQueries({ queryKey: ['isp-smartfi-billing'] });
         toast.success(r.subscription === 'paid' ? 'You are on Paid.' : 'You are on Free.');
      },
      onError: (e: unknown) =>
         toast.error(
            (e as { response?: { data?: { error?: string } } })?.response?.data
               ?.error ?? 'Could not switch.'
         ),
   });

   const convert = useMutation({
      mutationFn: () => convertCreditsForIsp(Number(credits)),
      onSuccess: (r) => {
         qc.invalidateQueries({ queryKey: ['isp-smartfi-billing'] });
         setCredits('');
         toast.success(
            `${r.creditsSpent} credit(s) → ${num0(r.pointsGranted)} points.`
         );
      },
      onError: (e: unknown) =>
         toast.error(
            (e as { response?: { data?: { error?: string } } })?.response?.data
               ?.error ?? 'Could not convert.'
         ),
   });

   if (isLoading || !data) {
      return (
         <IspPage>
            <Title>Subscriptions</Title>
            <SkeletonLines lines={3} />
         </IspPage>
      );
   }

   const short = data.shortfall > 0;
   const isFree = data.subscription !== 'paid';
   const ptsOf = (peso: number) => num0(Math.ceil(peso * data.pointsPerPeso));
   const firstTierPts = data.tiers.length ? ptsOf(data.tiers[0].pricePerAccount) : '—';
   /** "1–10 customers", "11–30 customers", "31+ customers" — the bands in words. */
   const priceRows = data.tiers.map((tier, i) => {
      const from = i === 0 ? 1 : data.tiers[i - 1].upTo + 1;
      return {
         label:
            tier.upTo > 0
               ? `${from}–${tier.upTo} customers`
               : `${from}+ customers`,
         pts: ptsOf(tier.pricePerAccount),
      };
   });
   /**
    * One worked example, priced the way the bill is: the first 3 free, then
    * every billed customer at the band that count falls in.
    */
   const example = (() => {
      if (!data.tiers.length) return null;
      const total = 15;
      const billed = total - 3;
      const band =
         data.tiers.find((x) => x.upTo > 0 && billed <= x.upTo) ??
         data.tiers.find((x) => x.upTo === 0) ??
         data.tiers[data.tiers.length - 1];
      return {
         total,
         billed,
         pts: num0(Math.ceil(billed * band.pricePerAccount * data.pointsPerPeso)),
      };
   })();

   return (
      <IspPage>
         <TopRow>
            <div>
               <Title>
                  Subscriptions
               </Title>
               <Note>
                  Your plan with SmartFi for ISP Business — not what you charge
                  your subscribers.
               </Note>
            </div>
         </TopRow>

         <Plans>
            <PlanCard>
               {isFree && <CurrentPill>YOUR PLAN</CurrentPill>}
               <PlanHead>
                  <PlanName>Free</PlanName>
                  <PlanPrice>
                     0 <PlanUnit>points · forever</PlanUnit>
                  </PlanPrice>
                  <PlanTag>Run up to 150 customers at no cost — you do the billing steps yourself.</PlanTag>
               </PlanHead>
               <PlanBody>
                  <Item $on><Ico $on>✓</Ico><span>Up to <b>150 customers</b></span></Item>
                  <Item $on><Ico $on>✓</Ico><span>Customers, plans and speed limits</span></Item>
                  <Item $on><Ico $on>✓</Ico><span>Invoices with your logo, PDF and print</span></Item>
                  <Item $on><Ico $on>✓</Ico><span><b>Customer portal</b> — customers see and pay their bill</span></Item>
                  <Item $on><Ico $on>✓</Ico><span>Cut-off customers see a <b>pay-your-bill page</b></span></Item>
                  <Item $on><Ico $on>✓</Ico><span>Payment proof upload — you approve it</span></Item>
                  <Item $on><Ico $on>✓</Ico><span>Data usage and online status per customer</span></Item>
                  <Item $on><Ico $on>✓</Ico><span>Router health — CPU, memory, uptime</span></Item>
                  <Item><Ico>✕</Ico><span>You cut off unpaid customers yourself</span></Item>
                  <Item><Ico>✕</Ico><span>You reconnect them after they pay</span></Item>
                  <Item><Ico>✕</Ico><span>You make invoices each month</span></Item>
                  <Item><Ico>✕</Ico><span>No reminder emails</span></Item>
                  {/* The current plan is a pill at the top, never a button
                      (Ray, 2026-10-30). */}
                  {!isFree && (
                     <PlanLink
                        type="button"
                        disabled={switchPlan.isPending}
                        onClick={() => switchPlan.mutate('free')}
                     >
                        Downgrade to Free
                     </PlanLink>
                  )}
               </PlanBody>
            </PlanCard>

            {/* 🔴 THE BANDS LIVE ON THIS CARD, never behind a button (Ray,
                2026-10-30: "no need another button because they dont
                understand… i want to show them on the paid plan card"). */}
            <PlanCard $paid>
               <Ribbon>{isFree ? '★ RECOMMENDED' : '★ YOUR PLAN'}</Ribbon>
               <PlanHead $paid>
                  <PlanName>Paid</PlanName>
                  <PlanPrice>
                     {firstTierPts} <PlanUnit>points per customer a month</PlanUnit>
                  </PlanPrice>
                  <PlanTag>Cheaper per customer as you grow. First 3 always free.</PlanTag>
                  {/* The bands as plain rows, plus one worked example — so
                      "how much will I pay?" is answered without maths. */}
                  <PriceRows>
                     {priceRows.map((r) => (
                        <PriceRow key={r.label}>
                           <span>{r.label}</span>
                           <b>{r.pts} points each</b>
                        </PriceRow>
                     ))}
                  </PriceRows>
                  {example && (
                     <PlanExample>
                        Example: {example.total} customers → you pay for{' '}
                        {example.billed} = <b>{example.pts} points a month</b>
                     </PlanExample>
                  )}
               </PlanHead>
               <PlanBody>
                  <Item $on><Ico $on>✓</Ico><span><b>Unlimited</b> customers</span></Item>
                  <Item $on><Ico $on>✓</Ico><span>Everything in Free</span></Item>
                  <Item $on><Ico $on>✓</Ico><span>Unpaid customers <b>cut off automatically</b></span></Item>
                  <Item $on><Ico $on>✓</Ico><span><b>Reconnected automatically</b> when they pay</span></Item>
                  <Item $on><Ico $on>✓</Ico><span>Monthly invoices <b>made for you</b></span></Item>
                  <Item $on><Ico $on>✓</Ico><span>Reminder emails before the due date</span></Item>
                  {isFree && (
                     <PlanBtn
                        type="button"
                        disabled={switchPlan.isPending}
                        onClick={() => switchPlan.mutate('paid')}
                     >
                        ⚡ Upgrade to Paid
                     </PlanBtn>
                  )}
               </PlanBody>
            </PlanCard>
         </Plans>

         {/* 🔴 POINTS, NOT PESOS. Ray, 2026-09-01: *"i dont like to show the
             pesos stats, it should be points stats."* Points are what actually
             leaves the operator's balance; the peso price is SmartFi's internal
             figure and lives in the admin settings. Two currencies on one
             screen made an operator convert in their head to check a number
             they never pay in. */}
         {/* 🔴 THREE COLUMNS, and the third is the whole height of the other
             two. Ray, 2026-09-03, after saying it three times: *"A B C got
             empty space because of the convert stat card height… the 1st
             column is A & B, 2nd column is C and the 4th new stat, the convert
             points card is the 5th one."*

             The wallet carries a balance, two actions and a link — far more
             than a single figure — so in a row of equal cards it stretched its
             neighbours and left a band of nothing under their numbers. Given
             its own full-height column, nothing stretches. */}
         <Cards>
            <Col>
               {/* 🔴 SAY THE FREE ONES ARE FREE. Without this an operator
                   with 3 free and 2 paying sees "2 active subscribers" beside
                   5 people on their Accounts page and reads it as a bug in our
                   billing — which is the worst thing a billing page can be
                   accused of. */}
               <Card>
                  <Big>{data.activeAccounts}</Big>
                  <Small>
                     billable subscribers
                     {data.freeActive > 0
                        ? ` · ${data.freeActive} free`
                        : data.freeSlotsLeft > 0
                          ? ` · ${data.freeSlotsLeft} free ${
                               data.freeSlotsLeft === 1 ? 'slot' : 'slots'
                            } left`
                          : ''}
                  </Small>
               </Card>
               <Card>
                  <Big>
                     {Math.ceil(data.pricePerAccount * data.pointsPerPeso)}
                  </Big>
                  <Small>
                     points each per month
                     {data.tier
                        ? data.tier.upTo > 0
                           ? ` · your band: up to ${data.tier.upTo}`
                           : ' · your band: above the table'
                        : ''}
                  </Small>
               </Card>
            </Col>

            <Col>
               <Card $accent>
                  <Big>{num0(data.monthlyPoints)}</Big>
                  <Small>points a month</Small>
               </Card>
               {/* The new fourth figure. Everything else on this page says what
                   the month COSTS; nothing said when it is taken. That is the
                   only thing here an operator can act on — and the one that
                   turns "covered" into "top up before Tuesday". */}
               <Card>
                  <Big $tone={nextCharge.days <= 3 && short ? 'bad' : undefined}>
                     {nextCharge.days}
                  </Big>
                  <Small>
                     {nextCharge.days === 1 ? 'day' : 'days'} until your next
                     charge · {nextCharge.label}
                  </Small>
               </Card>
            </Col>

            <Card $wallet>
               <WalletTop>
                  <div>
                     <Big $tone={short ? 'bad' : 'ok'}>
                        {num0(data.pointsBalance)}
                     </Big>
                     <Small>your balance</Small>
                  </div>
                  <WalletCredits>
                     <CreditNum>{num0(data.creditBalance)}</CreditNum>
                     <Small>credits</Small>
                  </WalletCredits>
               </WalletTop>

               {/* Full-width actions, one per line, exactly as he drew them.
                   The amount appears only once converting is what you are
                   doing — an input sitting there permanently is a field to
                   read past on a page you opened to check a balance. */}
               {converting ? (
                  <ConvertRow>
                     <Num
                        type="text"
                        inputMode="numeric"
                        placeholder="0"
                        autoFocus
                        value={credits}
                        onChange={(e) => setCredits(e.target.value)}
                        aria-label="Credits to convert"
                     />
                     <Primary
                        type="button"
                        disabled={
                           convert.isPending ||
                           !Number.isInteger(Number(credits)) ||
                           Number(credits) < 1 ||
                           Number(credits) > data.creditBalance
                        }
                        onClick={() => convert.mutate()}
                     >
                        {convert.isPending ? '…' : 'Convert'}
                     </Primary>
                     <Ghost type="button" onClick={() => setConverting(false)}>
                        Cancel
                     </Ghost>
                  </ConvertRow>
               ) : (
                  <WideBtn
                     type="button"
                     disabled={data.creditBalance < 1}
                     onClick={() => setConverting(true)}
                  >
                     Convert credit to points
                  </WideBtn>
               )}
               <WideGhost type="button" onClick={() => navigate('/partners')}>
                  Buy more credits
               </WideGhost>
               <WalletHint>
                  1 credit = {num0(data.ispPointsPerCredit)} points
                  {Number(credits) >= 1 &&
                  Number.isInteger(Number(credits)) &&
                  Number(credits) <= data.creditBalance
                     ? ` · ${credits} → ${num0(
                          Number(credits) * data.ispPointsPerCredit
                       )} pts`
                     : ''}
               </WalletHint>
            </Card>
         </Cards>

         {short ? (
            <ShortBox>
               <ShortTitle>
                  You are {num0(data.shortfall)} points short this month
               </ShortTitle>
               {/* Said first and said plainly. An operator reading a billing
                   warning assumes the worst, and the worst is not what happens. */}
               <ShortBody>
                  <strong>Your subscribers are not affected.</strong> Nobody gets
                  disconnected over this — they stay online exactly as they are.
                  The only thing you cannot do while short is <em>add</em> a new
                  subscriber.
               </ShortBody>
               <ShortBody>
                  You hold <strong>{num0(data.creditBalance)}</strong>{' '}
                  credit{data.creditBalance === 1 ? '' : 's'}
                  {data.creditBalance > 0 ? (
                     <>
                        {' '}
                        — worth{' '}
                        <strong>
                           {num0(data.creditsWorthPoints)} points
                        </strong>{' '}
                        if you convert them all.
                     </>
                  ) : (
                     '. Buy some below to top up.'
                  )}
               </ShortBody>
            </ShortBox>
         ) : (
            <OkBox>
               Covered for this month — {num0(data.pointsBalance)}{' '}
               points against a {num0(data.monthlyPoints)} point bill.
            </OkBox>
         )}



         {data.charges.length > 0 && (
            <>
               <SectionTitle>What you have been charged</SectionTitle>
               {data.owedPoints > 0 && (
                  <Note>
                     <strong>
                        {num0(data.owedPoints)} points due
                     </strong>{' '}
                     across {data.charges.filter((c) => c.status === 'unpaid').length}{' '}
                     unpaid month
                     {data.charges.filter((c) => c.status === 'unpaid').length === 1
                        ? ''
                        : 's'}
                     . These clear themselves, oldest first, as soon as you have
                     the points — nothing to press.
                  </Note>
               )}
               <TableWrap>
                  <Table>
                     <thead>
                        <tr>
                           <Th>Month</Th>
                           <Th>Subscribers</Th>
                           <Th>Each</Th>
                           <Th>Points charged</Th>
                           <Th>Status</Th>
                        </tr>
                     </thead>
                     <tbody>
                        {data.charges.map((c) => (
                           <tr key={c.period}>
                              <Td>{monthLabel(c.period)}</Td>
                              <Td>{c.accounts}</Td>
                              {/* Frozen at the rate in force that month, so an
                                  old bill never restates itself. */}
                              <Td>
                                 {num0(Math.ceil(
                                    (c.totalPoints / Math.max(1, c.accounts))
                                 ))}{' '}
                                 pts
                              </Td>
                              <Td>{num0(c.totalPoints)} pts</Td>
                              <Td>
                                 {c.status === 'paid' ? (
                                    <Ok>Paid</Ok>
                                 ) : c.status === 'waived' ? (
                                    <Muted>Waived</Muted>
                                 ) : (
                                    <Owing>Due</Owing>
                                 )}
                              </Td>
                           </tr>
                        ))}
                     </tbody>
                  </Table>
               </TableWrap>
            </>
         )}
      </IspPage>
   );
};

const WalletRow = styled.div`
   display: flex;
   align-items: center;
   gap: 6px;
   margin-top: 10px;
`;
const Num = styled.input`
   width: 58px;
   min-width: 0;
   font-family: inherit;
   font-size: 14px;
   padding: 8px 10px;
   border: 1px solid ${colors.GRAY12};
   border-radius: 8px;
   background: transparent;
   color: inherit;
`;
/** Centred under the actions it explains, which are full width. */
const WalletHint = styled.p`
   margin: 8px 0 0;
   font-size: 12px;
   line-height: 1.6;
   text-align: center;
   color: ${colors.GRAY5};
`;

const Ok = styled.span`
   font-weight: 700;
   color: ${colors.GREEN};
`;
const Owing = styled.span`
   font-weight: 700;
   color: ${colors.RED};
`;
const Muted = styled.span`
   color: ${colors.GRAY5};
`;

const WalletTop = styled.div`
   display: flex;
   align-items: flex-start;
   justify-content: space-between;
   gap: 10px;
`;
const WalletCredits = styled.div`
   text-align: right;
`;
const CreditNum = styled.div`
   font-size: 17px;
   font-weight: 800;
`;

export default IspSmartfiBilling;

const TopRow = styled.div`
   display: flex;
   justify-content: space-between;
   gap: 12px;
   flex-wrap: wrap;
`;
const Title = styled.h1`
   margin: 0;
   font-size: 20px;
   display: flex;
   align-items: center;
   gap: 10px;
`;
const Note = styled.p`
   margin: 6px 0 0;
   font-size: 12.5px;
   line-height: 1.6;
   color: ${colors.GRAY5};
   max-width: 72ch;
`;
/**
 * 🔴 THREE columns, explicitly. Never auto-fit: it silently gave 3 where 4
 * were meant once before, and Ray asked about it three times.
 */
const Cards = styled.div`
   display: grid;
   grid-template-columns: repeat(3, minmax(0, 1fr));
   align-items: start;
   gap: 12px;

   @media (max-width: 900px) {
      grid-template-columns: minmax(0, 1fr);
   }
`;
/** One column holding two stacked cards, so neither stretches to the wallet. */
const Col = styled.div`
   display: flex;
   flex-direction: column;
   gap: 12px;
   min-width: 0;
`;
const Card = styled.div<{ $accent?: boolean; $wallet?: boolean }>`
   padding: 14px 16px;
   border-radius: 12px;
   border: 1px solid
      ${({ $accent, $wallet }) =>
         $wallet
            ? `${colors.PRIMARY}55`
            : $accent
              ? `${colors.PRIMARY}40`
              : colors.GRAY12};

   /* 🔴 Ray, 2026-09-03: *"make background color gradient, and please dont
      allow extra empty space."* The wallet card carries a balance, an input,
      two buttons and a hint — far more than its neighbours — so on a wide grid
      it stretched them and left a band of nothing under the numbers. It sizes
      itself to its content now and the row stretches nothing. */
   background: ${({ $accent, $wallet }) =>
      $wallet
         ? `linear-gradient(160deg, ${colors.PRIMARY}1a 0%, ${colors.PRIMARY}05 55%, ${colors.WHITE} 100%)`
         : $accent
           ? `${colors.PRIMARY}0d`
           : colors.WHITE};

   ${({ $wallet }) =>
      $wallet
         ? `
   display: flex;
   flex-direction: column;
   justify-content: flex-start;`
         : ''}
`;

/** Full-width actions, one per line, exactly as Ray drew them. */
const wide = `
   display: block;
   width: 100%;
   margin-top: 8px;
   font-family: inherit;
   font-size: 13.5px;
   font-weight: 800;
   padding: 10px 12px;
   border-radius: 9px;
   cursor: pointer;

   &:disabled {
      opacity: 0.5;
      cursor: default;
   }
`;
const WideBtn = styled.button`
   ${wide}
   border: none;
   background: ${colors.PRIMARY};
   color: #fff;
`;
const WideGhost = styled.button`
   ${wide}
   border: 1px solid ${colors.PRIMARY}55;
   background: transparent;
   color: ${colors.PRIMARY};
`;
const ConvertRow = styled.div`
   display: flex;
   align-items: center;
   gap: 6px;
   margin-top: 8px;
`;
const Big = styled.div<{ $tone?: 'ok' | 'bad' }>`
   font-size: 20px;
   font-weight: 700;
   line-height: 1.15;
   color: ${({ $tone }) =>
      $tone === 'bad' ? colors.DANGER : $tone === 'ok' ? colors.SUCCESS : colors.MAIN_DARK};
`;
const Small = styled.div`
   margin-top: 3px;
   font-size: 11.5px;
   line-height: 1.4;
   color: ${colors.GRAY5};
`;
const ShortBox = styled.div`
   display: flex;
   flex-direction: column;
   gap: 8px;
   padding: 14px 16px;
   border-radius: 12px;
   border: 1px solid ${colors.ORANGE}55;
   background: ${colors.ORANGE}10;
`;
const ShortTitle = styled.div`
   font-size: 14px;
   font-weight: 700;
   color: ${colors.MAIN_DARK};
`;
const ShortBody = styled.p`
   margin: 0;
   font-size: 12.5px;
   line-height: 1.65;
   color: ${colors.GRAY5};
   max-width: 72ch;
`;
const OkBox = styled.div`
   padding: 12px 16px;
   border-radius: 12px;
   border: 1px solid ${colors.SUCCESS}55;
   background: ${colors.SUCCESS}10;
   font-size: 12.5px;
   color: ${colors.MAIN_DARK};
`;
const Row = styled.div`
   display: flex;
   gap: 8px;
   flex-wrap: wrap;
   margin-top: 2px;
`;
const SectionTitle = styled.h2`
   margin: 6px 0 0;
   font-size: 14px;
   font-weight: 700;
   color: ${colors.MAIN_DARK};
`;
const TableWrap = styled.div`
   overflow-x: auto;
   border: 1px solid ${colors.GRAY12};
   border-radius: 10px;
`;
const Table = styled.table`
   width: 100%;
   border-collapse: collapse;
   font-size: 13px;
`;
const Th = styled.th`
   text-align: left;
   padding: 10px 12px;
   font-size: 11px;
   font-weight: 600;
   color: ${colors.GRAY5};
   text-transform: uppercase;
   letter-spacing: 0.04em;
   background: ${colors.GRAY14};
   white-space: nowrap;
`;
const Td = styled.td<{ $on?: boolean; $muted?: boolean }>`
   padding: 9px 12px;
   border-top: 1px solid ${colors.GRAY13};
   white-space: nowrap;
   font-weight: ${({ $on }) => ($on ? 700 : 400)};
   color: ${({ $on, $muted }) =>
      $on ? colors.PRIMARY : $muted ? colors.GRAY5 : colors.MAIN_DARK};
`;
const Primary = styled.button`
   background: ${colors.PRIMARY};
   color: ${colors.WHITE};
   border: none;
   border-radius: 8px;
   padding: 8px 14px;
   font-size: 12px;
   font-weight: 700;
   cursor: pointer;
`;
const Ghost = styled.button`
   background: transparent;
   color: inherit;
   border: 1px solid ${colors.GRAY12};
   border-radius: 8px;
   padding: 8px 14px;
   font-size: 12px;
   font-weight: 600;
   cursor: pointer;
`;

/* ─── Free / Paid cards ───────────────────────────────────────── */

/**
 * Two columns, set explicitly; one on a phone. Both cards stretch to the same
 * height so the two lists line up.
 */
const Plans = styled.div`
   display: grid;
   grid-template-columns: repeat(2, minmax(0, 1fr));
   gap: 16px;
   align-items: stretch;
   margin: 4px 0 18px;

   @media (max-width: 720px) {
      grid-template-columns: minmax(0, 1fr);
   }
`;

/** Paid is the card the page wants you to look at: border, glow, gradient head. */
const PlanCard = styled.div<{ $paid?: boolean }>`
   position: relative;
   border-radius: 8px;
   overflow: hidden;
   display: flex;
   flex-direction: column;
   background: ${colors.WHITE};
   border: ${({ $paid }) =>
      $paid ? `2px solid ${colors.PRIMARY}` : `1px solid ${colors.GRAY12}`};
   box-shadow: ${({ $paid }) =>
      $paid ? `0 12px 32px -12px ${colors.PRIMARY}73` : 'none'};
`;

const PlanHead = styled.div<{ $paid?: boolean }>`
   padding: 20px 22px 16px;
   color: ${({ $paid }) => ($paid ? '#fff' : colors.MAIN_DARK)};
   background: ${({ $paid }) =>
      $paid ? `linear-gradient(135deg, ${colors.PRIMARY} 0%, #6d4dff 100%)` : 'transparent'};
`;

const Ribbon = styled.span`
   position: absolute;
   top: 14px;
   right: 14px;
   padding: 4px 10px;
   border-radius: 999px;
   background: #ffd34d;
   color: #3a2a00;
   font-size: 11px;
   font-weight: 800;
   letter-spacing: 0.04em;
`;

const PlanName = styled.div`
   font-size: 14px;
   font-weight: 800;
   letter-spacing: 0.08em;
   text-transform: uppercase;
   opacity: 0.85;
`;

const PlanPrice = styled.div`
   margin-top: 6px;
   font-size: 44px;
   font-weight: 900;
   line-height: 1;
   letter-spacing: -0.02em;
`;

const PlanUnit = styled.span`
   font-size: 14px;
   font-weight: 600;
   letter-spacing: 0;
   opacity: 0.85;
`;

const PlanTag = styled.div`
   margin-top: 6px;
   font-size: 13px;
   opacity: 0.9;
`;

/** The bands as rows — "1–10 customers · 45 points each". */
const PriceRows = styled.div`
   margin-top: 12px;
   border-radius: 6px;
   background: rgba(255, 255, 255, 0.14);
   border: 1px solid rgba(255, 255, 255, 0.3);
`;

const PriceRow = styled.div`
   display: flex;
   justify-content: space-between;
   gap: 12px;
   padding: 6px 12px;
   font-size: 13px;

   & + & {
      border-top: 1px solid rgba(255, 255, 255, 0.2);
   }
   b {
      font-weight: 800;
   }
`;

const PlanExample = styled.div`
   margin-top: 8px;
   font-size: 12px;
   opacity: 0.95;

   b {
      font-weight: 800;
   }
`;

const PlanBody = styled.div`
   flex: 1;
   display: flex;
   flex-direction: column;
   gap: 10px;
   padding: 16px 22px 20px;
`;

/** Included = dark text; something you do by hand = muted. */
const Item = styled.div<{ $on?: boolean }>`
   display: flex;
   align-items: center;
   gap: 10px;
   font-size: 15px;
   color: ${({ $on }) => ($on ? colors.MAIN_DARK : colors.GRAY5)};

   b {
      font-weight: 700;
   }
`;

/** ✓ in a green circle, ✕ in a grey one — big enough to read at a glance. */
const Ico = styled.span<{ $on?: boolean }>`
   flex: 0 0 22px;
   height: 22px;
   border-radius: 50%;
   display: grid;
   place-items: center;
   font-size: 13px;
   font-weight: 900;
   background: ${({ $on }) => ($on ? '#dcfce7' : '#f1f2f6')};
   color: ${({ $on }) => ($on ? '#16a34a' : '#9aa1b2')};
`;

/**
 * Gold, like the ribbon above it — never the app's blue. Ray, 2026-10-30: the
 * blue was the same as "Convert credit to points" right below, so the one
 * button this page exists for looked like any other. A slow shine says "this
 * one".
 */
const PlanBtn = styled.button`
   position: relative;
   overflow: hidden;
   margin-top: auto;
   width: 100%;
   padding: 14px 16px;
   border: none;
   border-radius: 8px;
   background: linear-gradient(135deg, #ffe17a 0%, #ffc02e 55%, #ff9f1a 100%);
   color: #3a2a00;
   font-size: 16px;
   font-weight: 900;
   letter-spacing: 0.01em;
   cursor: pointer;
   box-shadow: 0 10px 24px -10px rgba(255, 160, 26, 0.9);
   transition: transform 0.15s ease, box-shadow 0.15s ease;

   &::after {
      content: '';
      position: absolute;
      top: 0;
      left: -60%;
      width: 40%;
      height: 100%;
      background: linear-gradient(
         100deg,
         transparent 0%,
         rgba(255, 255, 255, 0.65) 50%,
         transparent 100%
      );
      animation: planShine 3.2s ease-in-out infinite;
   }
   @keyframes planShine {
      0%, 60% { left: -60%; }
      100% { left: 130%; }
   }
   @media (prefers-reduced-motion: reduce) {
      &::after { animation: none; display: none; }
   }

   &:hover {
      transform: translateY(-1px);
      box-shadow: 0 14px 28px -10px rgba(255, 160, 26, 1);
   }
   &:disabled {
      opacity: 0.6;
      cursor: default;
      transform: none;
   }
`;

/** "YOUR PLAN" on the Free card — the same spot as the Paid card's ribbon. */
const CurrentPill = styled.span`
   position: absolute;
   top: 14px;
   right: 14px;
   padding: 4px 10px;
   border-radius: 999px;
   background: ${colors.PRIMARY}14;
   color: ${colors.PRIMARY};
   font-size: 11px;
   font-weight: 800;
   letter-spacing: 0.04em;
`;

/** Going back to Free is possible, but never presented as a choice to make. */
const PlanLink = styled.button`
   margin-top: auto;
   align-self: center;
   border: none;
   background: none;
   padding: 6px;
   font-size: 13px;
   color: ${colors.GRAY5};
   text-decoration: underline;
   cursor: pointer;
`;
